import { type SiteId } from '@solinkcorp/api-types';
import { LoadingSpinner } from '@solinkcorp/solink-components';
import { Popover, PopoverContent, PopoverTrigger } from '@solinkcorp/solink-ui/components/Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@solinkcorp/solink-ui/components/Tooltip';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import classNames from 'classnames';
import { withAspen } from 'components/aspen/withAspen';
import { ChevronDownIcon, MapPinIcon } from 'lucide-react';
import { type SELECT_LOCATIONS_CONTEXT } from 'models/AnalyticsConstants';
import { type FC, memo, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import { type VideoDiscoverySite } from 'utils/types';
import styles from './LocationSelector.module.sass';
import LocationSelectorAspen from './LocationSelectorAspen';
import MultiLocationSelector from './MultiLocationSelector';
import SelectedLocations from './SelectedLocations';
import SingleLocationSelector from './SingleLocationSelector';
import { useLocationSelectorState } from './useLocationSelectorState';

const SPINNER_SIZE = 16;
const MINI_SPINNER_SIZE = 9;

export interface LocationSelectorProps {
  className?: string;
  /** Legacy only. Aspen inherits the theme from its container. */
  colorMode?: 'dark' | 'light';
  /** PopoverContent portals to `document.body`, so a theme class on the trigger does not reach it. */
  contentClassName?: string;
  locations?: readonly VideoDiscoverySite[];
  selectedLocationId?: SiteId;
  selectedLocationIds?: readonly SiteId[];
  selectedLocationTags?: readonly string[];
  onLocationSelected?: (locationId: SiteId) => void;
  onLocationsSelected?: ({
    locationIds,
    locationTags,
  }: {
    locationIds: readonly SiteId[];
    locationTags?: readonly string[];
  }) => void;
  multiSelect?: boolean;
  context?:
    typeof SELECT_LOCATIONS_CONTEXT.GLOBAL_SELECTOR | typeof SELECT_LOCATIONS_CONTEXT.WIDGET;
  disabled?: boolean;
}

const LocationSelector: FC<LocationSelectorProps> = (props) => {
  const locale = useLocale();
  const {
    context,
    className = '',
    colorMode = 'dark',
    contentClassName,
    locations,
    multiSelect,
    onLocationSelected,
    onLocationsSelected,
    selectedLocationTags,
    selectedLocationId,
    selectedLocationIds,
    disabled,
  } = props;
  const [isLocationListOpen, setLocationListOpen] = useState(false);

  const {
    masterLocationsList,
    ready,
    fetching,
    hasAccessToLocations,
    locationsWithoutNoCamerasVideoPage,
  } = useLocationSelectorState(locations);

  const closeLocationList = () => setLocationListOpen(false);

  const locationSelectorClass = classNames(
    className,
    styles.locationSelectorContainer,
    styles[`mode-${colorMode}`],
    {
      [styles.open]: isLocationListOpen,
      [styles.ready]: ready,
      [styles.noLocations]: !hasAccessToLocations,
      [styles.filtered]: multiSelect
        ? !selectedLocationIds?.length || !selectedLocationIds?.includes(ALL_LOCATIONS as SiteId)
        : !selectedLocationId || selectedLocationId !== ALL_LOCATIONS,
    },
  );

  return (
    <Popover open={isLocationListOpen} onOpenChange={setLocationListOpen}>
      <PopoverTrigger asChild>
        <div className={cn('tw', locationSelectorClass)} data-testid="location-picker-container">
          <button
            className={styles.selector}
            data-testid="location-selector-trigger"
            disabled={disabled}
          >
            {ready && fetching ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative !w-auto">
                    <MapPinIcon className="size-4" />
                    <LoadingSpinner
                      spinnerStyle={{ width: MINI_SPINNER_SIZE }}
                      style={{ height: MINI_SPINNER_SIZE }}
                      colorMode="dark"
                      className="absolute -top-1 right-0"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{locale.locationSelector.fetchingLatest}</TooltipContent>
              </Tooltip>
            ) : (
              <MapPinIcon className="size-4" />
            )}
            <div className={styles['location-selector']}>
              <SelectedLocations
                locationTags={selectedLocationTags}
                locationIds={
                  selectedLocationIds || (selectedLocationId ? [selectedLocationId] : undefined)
                }
                locationsList={masterLocationsList}
                loading={!ready}
                noLocationAccess={!hasAccessToLocations}
                maxCharacterLimit={40}
              />
            </div>
            {ready ? (
              <ChevronDownIcon className="size-4" />
            ) : (
              <LoadingSpinner
                spinnerStyle={{ width: SPINNER_SIZE }}
                style={{ marginLeft: 10, height: SPINNER_SIZE }}
                colorMode="dark"
              />
            )}
          </button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn('tw !w-auto !p-0', contentClassName)}
        side="bottom"
        align="start"
        collisionPadding={4}
      >
        {multiSelect ? (
          <MultiLocationSelector
            locations={locationsWithoutNoCamerasVideoPage}
            locale={locale}
            selectedLocationIds={selectedLocationIds}
            selectedLocationTags={selectedLocationTags}
            onLocationsSelected={onLocationsSelected}
            closeLocationList={closeLocationList}
            context={context}
            isFetching={fetching}
          />
        ) : (
          <SingleLocationSelector
            locations={masterLocationsList}
            locale={locale}
            selectedLocationId={selectedLocationId}
            onLocationSelected={onLocationSelected}
            closeLocationList={closeLocationList}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default withAspen(memo(LocationSelector), LocationSelectorAspen);
